import { useEffect, useState } from 'react';
import Api from '../../Api';
import { getFirstCarouselPhotoUrl } from '../resolvePhotoUrl';

const photoCache = new Map();
const pendingFetches = new Map();

function fetchEventPhoto(eventId) {
  if (photoCache.has(eventId)) {
    return Promise.resolve(photoCache.get(eventId));
  }

  if (pendingFetches.has(eventId)) {
    return pendingFetches.get(eventId);
  }

  const promise = Api.getEvent({ id: eventId })
    .then(({ data }) => {
      const url = data?.status === 'ok'
        ? getFirstCarouselPhotoUrl(data.event, Api.url)
        : null;
      photoCache.set(eventId, url);
      pendingFetches.delete(eventId);
      return url;
    })
    .catch(() => {
      photoCache.set(eventId, null);
      pendingFetches.delete(eventId);
      return null;
    });

  pendingFetches.set(eventId, promise);
  return promise;
}

export default function useEventCardPhoto(eventId, item, containerRef) {
  const inlineUrl = getFirstCarouselPhotoUrl(item, Api.url);
  const cachedUrl = photoCache.get(eventId);
  const [photoUrl, setPhotoUrl] = useState(inlineUrl || cachedUrl || null);
  const [shouldLoad, setShouldLoad] = useState(Boolean(inlineUrl || photoCache.has(eventId)));

  useEffect(() => {
    if (inlineUrl) {
      photoCache.set(eventId, inlineUrl);
      setPhotoUrl(inlineUrl);
      return;
    }

    if (photoCache.has(eventId)) {
      setPhotoUrl(photoCache.get(eventId));
      return;
    }

    const element = containerRef?.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, { rootMargin: '120px' });

    observer.observe(element);
    return () => observer.disconnect();
  }, [eventId, inlineUrl, containerRef]);

  useEffect(() => {
    if (!shouldLoad || inlineUrl || photoCache.has(eventId)) return undefined;

    let cancelled = false;

    fetchEventPhoto(eventId).then((url) => {
      if (!cancelled) setPhotoUrl(url);
    });

    return () => {
      cancelled = true;
    };
  }, [shouldLoad, eventId, inlineUrl]);

  return photoUrl;
}
